import React, { useEffect, useState, Fragment} from 'react'
import { useNotify, useTranslate } from 'react-admin'
import { ShowCard } from 'src/components/uiElements/cards/ShowCard'
import { LoadingButton } from '@mui/lab'
import { currencyServices } from 'src/services/payment/currency/currency.service'
import RefreshIcon from '@mui/icons-material/Refresh';
import { HasPermission, hasPermissionFunction } from 'src/helper/HasPermission'
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  TablePagination,
  TableFooter,
  Skeleton,
  Button,
} from '@mui/material'
import { paymentServices } from 'src/services/payment/Payment.service';
import { numberFormatterWithDecimalAndThousand } from 'src/helper/numberFormatterWithDecimalAndThousand'
import { parseDateFormat } from 'src/helper/parseDateFormat'
import { ErrorHandler } from 'src/helper/ErrorHandler'
import { StatusTableCell } from '../../../helper/StatusTableCell'

export const ReturnsTable = ({hash_reservation}) => {
    const translate = useTranslate()
    const notify = useNotify()
    const [registers, setRegisters] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [currencyMap, setCurrencyMap] = useState([])
    const [isConfirmLoading, setIsConfirmLoading] = useState(false)
    const [isCancelLoading, setIsCancelLoading] = useState(false)
    const [tmaError, setTmaError] = useState(null)
    const [paginationCreditData, setPaginationCreditData] = useState({ page: 0, rowsPerPage: 10 })
    const getData = async () =>{
      setIsLoading(true)
      paymentServices.getReturned(hash_reservation).then(res=>{
        setRegisters(res)
      }).finally(()=>{
        setIsLoading(false)
      })
    }
    const getCurrency = () =>{
        currencyServices.getByAll().then((res)=>{
          setCurrencyMap(res)
        })
    }
    useEffect(()=>{
      getCurrency()
      getData()
    },[])

    const onPageCreditChange = (number) => {
      const paginationCreditDataAux = { ...paginationCreditData }
      paginationCreditDataAux.page = number
      setPaginationCreditData(paginationCreditDataAux)
    }
  
    const onRowsPerPageCreditChange = (e) => {
      const paginationCreditDataAux = { ...paginationCreditData }
      paginationCreditDataAux.rowsPerPage = parseInt(e.target.value, 10)
      paginationCreditDataAux.page = 0
      setPaginationCreditData(paginationCreditDataAux)
    }

    const onConfirm = async (id) =>{
      setIsConfirmLoading(true)
      await paymentServices.confirmReimbursement(id).finally(()=>{
        setIsConfirmLoading(false)
      }).catch((res)=>{
        if(res?.response?.status || res?.status_code){
          setTmaError(res)
        }
        notify('',{type:'error'})
      }).then(()=>{
        getData()
      })
    }

    const onCancel = async(id) =>{
      setIsCancelLoading(true)
      await paymentServices.cancelReimbursement(id).finally(()=>{
        setIsCancelLoading(false)
      }).catch(()=>{
        notify('',{type:'error'})
      }).then(()=>{
        getData()
      })
    }

    const mapCurrency = (id) =>{
      return currencyMap.find(currency => currency.id == id)?.short_name ?? ''
    }
  return (
    <>
        <ShowCard 
            title={translate('ra.title.returns')}
            preActionButtonJustify='flex-end'
            preActionButton={
              <LoadingButton loading={isLoading} onClick={getData}>
                <RefreshIcon/>
              </LoadingButton>
            }
          >
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate('ra.field.status')}</TableCell>
                    <TableCell>{translate('ra.field.currency')}</TableCell>
                    <TableCell>{translate('ra.field.amount_used')}</TableCell>
                    <TableCell>{translate('ra.field.date')}</TableCell>
                    <TableCell>{translate('ra.action.actions')}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading? (
                    <TableRow>
                      {[...Array(3)].map((_, i) => (
                        <TableCell key={i}>
                          <Skeleton variant='rectangular' width={'100%'} height={20} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ) : (
                    (paginationCreditData.rowsPerPage > 0
                      ? registers?.slice(
                          paginationCreditData.page * paginationCreditData.rowsPerPage,
                          paginationCreditData.page * paginationCreditData.rowsPerPage +
                            paginationCreditData.rowsPerPage
                        )
                      : registers
                    )?.map((row, i) => {
                      return (
                        <Fragment key={i}>
                          <TableRow>
                            <TableCell>
                              <StatusTableCell data={row?.status}/>
                            </TableCell>
                            <TableCell>{mapCurrency(row?.currency_id)}</TableCell>
                            <TableCell>{numberFormatterWithDecimalAndThousand(row?.amount)}</TableCell>
                            <TableCell>{parseDateFormat(row?.created_at)}</TableCell>
                            <TableCell>
                              <div style={{display:'flex', flexDirection:'row'}}>
                                  <LoadingButton
                                      variant='outlined'
                                      disabled = {row.status !== 'PENDING' || !hasPermissionFunction('reimbursement', 'confirm')}
                                      size='small'
                                      loading = {isConfirmLoading}
                                      sx={{ marginRight: '5px' }}
                                      onClick={async () => {onConfirm(row.id)}}
                                  >
                                    {translate('ra.action.to_confirm')}
                                  </LoadingButton>
                                  {<LoadingButton
                                      variant='outlined'
                                      size='small'
                                      disabled = {row.status !== 'PENDING' || !hasPermissionFunction('reimbursement', 'cancel')}
                                      loading={isCancelLoading}
                                      sx={{ marginRight: '5px' }}
                                      onClick={async () => {onCancel(row.id)}}
                                  >
                                    {translate('ra.action.cancel')}
                                  </LoadingButton>}
                              </div>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      )
                    })
                  )}
                </TableBody>

                <TableFooter>
                  <TableRow>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      colSpan={12}
                      count={registers.length}
                      rowsPerPage={paginationCreditData.rowsPerPage}
                      page={paginationCreditData.page}
                      onPageChange={(e, number) => onPageCreditChange(number)}
                      onRowsPerPageChange={(e) => onRowsPerPageCreditChange(e)}
                      labelRowsPerPage={translate('ra.navigation.page_rows_per_page')}
                      labelDisplayedRows={({ from, to, count }) =>
                        `${from}–${to} ${translate('ra.navigation.of')} ${count}`
                      }
                    />
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </ShowCard>
          {tmaError && <ErrorHandler error={tmaError} callback={''}/>}
    </>
  )
}
